// REACT AND REACT HOOKS
import React, { useState, useEffect, useRef } from 'react';

import Select from 'react-select'

// BOOTSTRAP COMPONENTS for modal and buttons
import { Modal, Button } from 'react-bootstrap';

// DATATABLES LIBRARY for displaying tables (not used in this modal, but imported for consistency)
import DataTable from 'datatables.net-react';

// CURRENCY INPUT for handling currency fields
import CurrencyInput from 'react-currency-input-field';

// UTILITY FUNCTIONS for extracting plain item names
import { getPlainName } from '../FP_foodPantryUtils';

// TABLE COLUMN CONFIGURATIONS (not used directly here, but available for future table displays)
import { columnsInventory, columnsTransactions } from '../FP_foodPantryTables';

import introJs from 'intro.js';

// Modal for creating new categories, items, and variations in the food pantry system
export function CreateItemsAndVariationsModal({
  show, // Boolean to show/hide modal
  onHide, // Function to close modal
  selectedOrgID, // Organization ID for API calls
  handleInputChange, // Generic input change handler
  showReceiveItemSuccessBanner, // Function to show success notification
  foodPantryTourStager, // Current stage of the food pantry tour
  setFoodPantryTourStager // Function to update the food pantry tour stage
}) {

  const url = process.env.REACT_APP_BACKURL;
  // Controls which form is shown: Category, Item, or Variation
  const [selectedOption, setSelectedOption] = useState("Category");

  // Full lists fetched from backend for suggestions and validation
  const [allItems, setAllItems] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [allVariations, setAllVariations] = useState([]);

  // Filtered suggestions for dropdowns/autoComplete
  const [itemSuggestions, setItemSuggestions] = useState([]);
  const [categorySuggestions, setCategorySuggestions] = useState([]);
  const [variationSuggestions, setVariationSuggestions] = useState([]);

  // Add state for code type selector (upc, plu, otherCode)
  const [variationCodeType, setVariationCodeType] = useState("upc");

  // Form state for each creation type
  const [variationForm, setVariationForm] = useState({
    itemName: "",
    variationName: "",
    suggestedPrice: "0.00",
    upc: "",
    plu: "",
    otherCode: "",
  });

  const [itemForm, setItemForm] = useState({
    name: "",
    description: "",
    categoryName: "",
    suggestedPrice: "0.00",
    unit: "1",
    unitName: "",
    packSize: "1",
  });

  const [categoryForm, setCategoryForm] = useState({
    name: "",
    parentName: "",
  });


  // Refs for dropdown closing logic
  const itemNameInputRef = useRef(null);
  const variationNameInputRef = useRef(null);
  const itemFormCategoryRef = useRef(null);

  // Fetch all lists (items, categories, variations) when modal opens
  useEffect(() => {
    if (show) {
      // Fetch all items for suggestions and validation
      fetch(url + '/api/foodPantry/inventory/' + selectedOrgID + '/items', {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        method: 'GET'
      })
        .then(res => res.json())
        .then(data => setAllItems(Array.isArray(data) ? data : []))
        .catch(() => setAllItems([]));

      // Fetch all categories for suggestions and validation
      fetch(url + '/api/foodPantry/itemCategory/' + selectedOrgID, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        method: 'GET'
      })
        .then(res => res.json())
        .then(data => setAllCategories(Array.isArray(data.categories) ? data.categories : []))
        .catch(() => setAllCategories([]));

      // Fetch all variations for suggestions and validation
      fetch(url + '/api/foodPantry/inventory/' + selectedOrgID + '/all-variations', {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        method: 'GET'
      })
        .then(res => res.json())
        .then(data => setAllVariations(Array.isArray(data) ? data : []))
        .catch(() => setAllVariations([]));
    }
    // Reset all form and suggestion state when modal closes
    if (!show) {
      setVariationForm({ itemName: "", variationName: "", suggestedPrice: "0.00", upc: "" });
      setItemForm({ name: "", description: "", categoryName: "", suggestedPrice: "0.00", unit: "1", unitName: "", packSize: "1" });
      setCategoryForm({ name: "", parentName: "" });
      setItemSuggestions([]);
      setCategorySuggestions([]);
      setVariationSuggestions([]);
      setSelectedOption("Category");
    }
  }, [show, selectedOrgID]);

  // Close dropdown suggestions when clicking outside input fields
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        itemNameInputRef.current && !itemNameInputRef.current.contains(event.target)
      ) setItemSuggestions([]);
      if (
        variationNameInputRef.current && !variationNameInputRef.current.contains(event.target)
      ) setVariationSuggestions([]);
      if (
        itemFormCategoryRef.current && !itemFormCategoryRef.current.contains(event.target)
      ) setCategorySuggestions([]);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // --- Handlers for filtering suggestions ---

  // Filter item suggestions for the variation form based on input
  const handleVariationItemNameChange = (e) => {
    const value = e.target.value;
    setVariationForm({ ...variationForm, itemName: value });
    if (value.length > 0) {
      setItemSuggestions(
        allItems.filter(item =>
          item.name.toLowerCase().includes(value.toLowerCase())
        ).slice(0, 8)
      );
    } else {
      setItemSuggestions([]);
    }
  };

  // Filter variation suggestions for the variation form based on input and selected item
  const handleVariationNameChange = (e) => {
    const value = e.target.value;
    setVariationForm({ ...variationForm, variationName: value });
    const selectedItem = allItems.find(
      item => item.name.toLowerCase() === (variationForm.itemName || "").toLowerCase()
    );
    if (selectedItem && value.length > 0) {
      setVariationSuggestions(
        allVariations.filter(
          v => v.itemID === selectedItem.itemID &&
            v.name.toLowerCase().includes(value.toLowerCase())
        ).slice(0, 8)
      );
    } else {
      setVariationSuggestions([]);
    }
  };

  // Filter category suggestions for the item form based on input
  const handleItemFormInputChange = (e) => {
    setItemForm({ ...itemForm, [e.target.name]: e.target.value });
    if (e.target.name === "categoryName") {
      const value = e.target.value;
      if (value.length > 0) {
        setCategorySuggestions(
          allCategories.filter(category =>
            category.name.toLowerCase().includes(value.toLowerCase())
          ).slice(0, 8)
        );
      } else {
        setCategorySuggestions([]);
      }
    }
  };

  // --- Dropdown click handlers ---
  // Set selected item from suggestions
  const handleItemSuggestionClick = (item) => {
    setVariationForm({ ...variationForm, itemName: item.name });
    setItemSuggestions([]);
  };
  // Set selected variation from suggestions
  const handleVariationSuggestionClick = (variation) => {
    setVariationForm({ ...variationForm, variationName: variation.name });
    setVariationSuggestions([]);
  };
  // Set selected category from suggestions
  const handleCategorySuggestionClick = (category) => {
    setItemForm({ ...itemForm, categoryName: category.name });
    setCategorySuggestions([]);
  };

  // --- Submit handlers ---

  // Handle submitting the variation creation form
  const handleVariationSubmit = async (e) => {
    e.preventDefault();
    // Find the item by name (case-insensitive)
    const item = allItems.find(i => i.name.toLowerCase() === variationForm.itemName.trim().toLowerCase());
    if (!item) {
      showReceiveItemSuccessBanner(
        "Item Not Found",
        <span>Item does not exist. Please select an existing item.</span>,
        "alert"
      );
      return;
    }

    // Check for duplicate variation name for this item
    const duplicateVariation = allVariations.find(
      v =>
        v.itemID === item.itemID &&
        v.name.trim().toLowerCase() === variationForm.variationName.trim().toLowerCase()
    );
    if (duplicateVariation) {
      showReceiveItemSuccessBanner(
        "Duplicate Variation",
        <span>Variation with this name already exists for this item.</span>,
        "alert"
      );
      return;
    }

    // Format price and validate
    const price = Number(variationForm.suggestedPrice);
    if (isNaN(price) || variationForm.suggestedPrice === "") {
      showReceiveItemSuccessBanner(
        "Invalid Price",
        <span>Suggested price must be a valid number.</span>,
        "alert"
      );
      return;
    }

    // Validate code field
    const codeValue = variationForm[variationCodeType];
    if (!codeValue) {
      showReceiveItemSuccessBanner(
        "Missing Code",
        <span>Please enter a value for <strong>{variationCodeType.toUpperCase()}</strong>.</span>,
        "alert"
      );
      return;
    }

    // Compose variation data object for API
    const variationData = {
      itemID: item.itemID,
      name: variationForm.variationName.trim(),
      suggestedPrice: price,
      upc: variationCodeType === "upc" ? codeValue : "",
      plu: variationCodeType === "plu" ? codeValue : "",
      otherCode: variationCodeType === "otherCode" ? codeValue : "",
      type: variationCodeType,
    };

    try {
      const url = process.env.REACT_APP_BACKURL;
      const response = await fetch(
        `${url}/api/foodPantry/inventory/${selectedOrgID}/create-variation`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ variation: variationData })
        }
      );
      const result = await response.json();
      if (!response.ok) {
        showReceiveItemSuccessBanner(
          "Variation Error",
          <span>{result.message || "Failed to create variation."}</span>,
          "extreme-error"
        );
        return;
      }
      // Show success banner and reset form
      showReceiveItemSuccessBanner(
        "Success",
        <span>
          Variation <strong>{variationForm.variationName}</strong> for <strong>{variationForm.itemName}</strong> created.
        </span>,
        "success"
      );
      setVariationForm({ ...variationForm, variationName: "", suggestedPrice: "0.00", upc: "", plu: "", otherCode: "" });
    } catch (error) {
      showReceiveItemSuccessBanner(
        "Critical Error",
        <span>Error creating variation.</span>,
        "extreme-error"
      );
    }
  };

  // Handle submitting the item creation form
  const handleItemSubmit = async (e) => {
    e.preventDefault();
    // Check for duplicate item name in all inventory
    const duplicateItem = allItems.find(
      item => item.name.trim().toLowerCase() === itemForm.name.trim().toLowerCase()
    );
    if (duplicateItem) {
      showReceiveItemSuccessBanner(
        "Duplicate Item",
        <span>An item named <strong>{itemForm.name}</strong> already exists in the inventory. Please choose a different name.</span>,
        "alert"
      );
      return;
    }

    // Find category by name
    const foundCat = allCategories.find(cat => cat.name.trim().toLowerCase() === itemForm.categoryName.trim().toLowerCase());
    if (!foundCat) {
      showReceiveItemSuccessBanner(
        "Category Not Found",
        <span>Category not found. Please create the category first.</span>,
        "alert"
      );
      return;
    }

    // Format price and validate
    const price = parseFloat(Number(itemForm.suggestedPrice).toFixed(2));
    if (isNaN(price)) {
      showReceiveItemSuccessBanner(
        "Invalid Price",
        <span>Suggested price must be a valid number.</span>,
        "alert"
      );
      return;
    }

    // Compose item data object for API
    const itemData = {
      name: itemForm.name.trim(),
      description: itemForm.description,
      itemCategoryID: foundCat.itemCategoryID,
      suggestedPrice: price,
      unitQty: `${itemForm.unit}${['oz', 'ct', 'g', 'kg', 'qt', 'lt'].includes(itemForm.unitName) ? '' : ' '
        }${itemForm.unitName}`.trim(),
      packSize: itemForm.packSize === "" ? 1 : Number(itemForm.packSize),
    };
    console.log(itemForm.unitName)

    try {
      const url = process.env.REACT_APP_BACKURL;
      const response = await fetch(
        `${url}/api/foodPantry/inventory/${selectedOrgID}/create-item`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ item: itemData })
        }
      );
      const result = await response.json();
      if (!response.ok) {
        showReceiveItemSuccessBanner(
          "Item Error",
          <span>{result.message || "Failed to create item."}</span>,
          "extreme-error"
        );
        return;
      }
      showReceiveItemSuccessBanner(
        "Success",
        <span>
          Item <strong>{itemForm.name}</strong> added to <strong>{itemForm.categoryName}</strong>.
        </span>,
        "success"
      );
      setItemForm({ name: "", description: "", categoryName: "", suggestedPrice: "0.00", unit: "1", unitName: "", packSize: "1" });
      // Refresh items list after creation
      fetch(url + '/api/foodPantry/inventory/' + selectedOrgID + '/items', {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        method: 'GET'
      })
        .then(res => res.json())
        .then(data => setAllItems(Array.isArray(data) ? data : []));
    } catch (error) {
      showReceiveItemSuccessBanner(
        "Critical Error",
        <span>Error creating item.</span>,
        "extreme-error"
      );
    }
  };

  // Handle submitting the category creation form
  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    // Find parent category if provided
    let parentCat = null;
    if (categoryForm.parentName && categoryForm.parentName.trim() !== "") {
      parentCat = allCategories.find(
        cat => (cat.name && cat.name.trim().toLowerCase()) === categoryForm.parentName.trim().toLowerCase() && cat.itemCategoryID
      );
      if (!parentCat) {
        showReceiveItemSuccessBanner(
          "Parent Category Not Found",
          <span>Parent category not found.</span>,
          "alert"
        );
        return;
      }
    }

    // Check for duplicate category name
    const duplicateCategory = allCategories.find(
      cat => (cat.name && cat.name.trim().toLowerCase()) === categoryForm.name.trim().toLowerCase()
    );
    if (duplicateCategory) {
      showReceiveItemSuccessBanner(
        "Duplicate Category",
        <span>Category with this name already exists.</span>,
        "alert"
      );
      return;
    }

    // Compose category object for API
    const newCategory = {
      name: categoryForm.name.trim(),
      parentID: parentCat && parentCat.itemCategoryID ? parentCat.itemCategoryID : null,
      level: parentCat && typeof parentCat.level === "number" ? (parentCat.level + 1) : 0,
    };

    try {
      const url = process.env.REACT_APP_BACKURL;
      const response = await fetch(`${url}/api/foodPantry/itemCategory/${selectedOrgID}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ category: newCategory }),
      });
      const result = await response.json();

      if (!response.ok) {
        showReceiveItemSuccessBanner(
          "Category Error",
          <span>{result.message || "Failed to create category."}</span>,
          "extreme-error"
        );
        return;
      }

      showReceiveItemSuccessBanner(
        "Success",
        categoryForm.parentName ? (
          <span>
            Category <strong>{categoryForm.name}</strong> added under <strong>{categoryForm.parentName}</strong>.
          </span>
        ) : (
          <span>
            Category <strong>{categoryForm.name}</strong> created.
          </span>
        ),
        "success"
      );
      setCategoryForm({ name: "", parentName: "" });
      // Refresh categories list after creation
      fetch(url + `/api/foodPantry/itemCategory/${selectedOrgID}`, {
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        method: "GET",
      })
        .then((res) => res.json())
        .then((data) => setAllCategories(Array.isArray(data.categories) ? data.categories : []));
    } catch (error) {
      showReceiveItemSuccessBanner(
        "Critical Error",
        <span>Error creating category.</span>,
        "extreme-error"
      );
    }
  };

  // Handler for the triple selector
  const handleVariationCodeTypeChange = (type) => {
    setVariationCodeType(type);
    // Clear other code fields when switching
    setVariationForm((prev) => ({
      ...prev,
      upc: type === "upc" ? prev.upc : "",
      plu: type === "plu" ? prev.plu : "",
      otherCode: type === "otherCode" ? prev.otherCode : "",
    }));
  };

  // Handler for the code input field
  const handleVariationCodeInputChange = (e) => {
    const { name, value } = e.target;
    setVariationForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Track if the selector tour step has already been shown for this modal open
  const [hasShownSelectorTour, setHasShownSelectorTour] = useState(false);


  // CATEGORY TOUR (with selector step)
  useEffect(() => {
    if (
      foodPantryTourStager === "Create" &&
      show &&
      !hasShownSelectorTour
    ) {
      setTimeout(() => {
        const intro = introJs();
        intro.setOptions({
          steps: [
            {
              element: '#category-item-variation-selector',
              intro: 'Use this selector to switch between creating a Category, Item, or Variation.',
              hidePrev: true
            }
          ],
          exitOnOverlayClick: false,
          showBullets: false
        });
        intro.oncomplete(() => {
          setHasShownSelectorTour(true);
        });
        intro.onexit(() => {
          setHasShownSelectorTour(true);
        });
        intro.start();
      }, 350);
    }
  }, [foodPantryTourStager, show, hasShownSelectorTour]);

  // CATEGORY TOUR (actual fields)
  useEffect(() => {
    if (
      foodPantryTourStager === "Create" &&
      show &&
      selectedOption === "Category" &&
      hasShownSelectorTour
    ) {
      setTimeout(() => {
        const intro = introJs();
        intro.setOptions({
          steps: [
            {
              element: '#category-name-input',
              intro: 'Enter the name for your new category here.',
              hidePrev: true
            },
            {
              element: '#parent-category-input',
              intro: 'Optionally enter the name of an already existing category as the parent.',
              hidePrev: true
            }
          ],
          exitOnOverlayClick: false,
          showBullets: false
        });
        intro.oncomplete(() => {
          setSelectedOption("Item");
        });
        intro.start();
      }, 350);
    }
  }, [foodPantryTourStager, show, selectedOption, setFoodPantryTourStager, hasShownSelectorTour]);


  // ITEM TOUR
  useEffect(() => {
    if (
      foodPantryTourStager === "Create" &&
      show &&
      selectedOption === "Item"
    ) {
      setTimeout(() => {
        const itemIntro = introJs();
        itemIntro.setOptions({
          steps: [
            {
              element: '#item-name-and-desc',
              intro: 'Enter the item name and an optional description.',
              hidePrev: true
            },
            {
              element: '#item-category-select',
              intro: 'Select the category the item should be in.',
              hidePrev: true
            },
            {
              element: '#item-price-input',
              intro: 'Enter the average price per unit of this item.',
              hidePrev: true
            },
            {
              element: '#item-other-fields',
              intro: 'Specify the size, unit of measure, and pack size for this item.',
              hidePrev: true
            }
          ],
          exitOnOverlayClick: false,
          showBullets: false
        });
        itemIntro.oncomplete(() => {
          setSelectedOption("Variation");
          setFoodPantryTourStager && setFoodPantryTourStager("Create Item Complete"); // <-- ADD THIS LINE
        });
        itemIntro.start();
      }, 350);
    }
  }, [foodPantryTourStager, show, selectedOption, setFoodPantryTourStager]);

  // VARIATION TOUR
  useEffect(() => {
    if (
      foodPantryTourStager === "Create Item Complete" && // <-- changed from "Create"
      show &&
      selectedOption === "Variation"
    ) {
      setTimeout(() => {
        const variationIntro = introJs();
        variationIntro.setOptions({
          steps: [
            {
              element: '#variation-name-and-item',
              intro: 'Enter the new variation name and the name of an already existing item.',
              hidePrev: true
            },
            {
              element: '#variation-code-input',
              intro: 'Enter the code you want the item to be identified as by using the selector.',
              hidePrev: true
            },
            {
              element: '#variation-price-input',
              intro: 'Enter the average unit price for this variation.',
              hidePrev: true
            }
          ],
          exitOnOverlayClick: false,
          showBullets: false
        });
        variationIntro.oncomplete(() => {
          //hide modal after completing variation tour
          onHide();
          setFoodPantryTourStager && setFoodPantryTourStager("Item Details Intro");
        });
        variationIntro.start();
      }, 350);
    }
  }, [foodPantryTourStager, show, selectedOption, setFoodPantryTourStager]);



  // Helper to start the tour for the current selection, always starting with the selector
  const startTourForCurrentSelection = () => {
    const intro = introJs();
    intro.setOptions({
      steps: [
        {
          element: '#category-item-variation-selector',
          intro: 'Use this selector to switch between creating a Category, Item, or Variation.',
          hidePrev: true
        }
      ],
      exitOnOverlayClick: false,
      showBullets: false
    });
    intro.oncomplete(() => {
      // After selector, show the relevant section tour
      startSectionTour(selectedOption);
    });
    // Only start section tour on complete, not on exit, to avoid double tour
    // intro.onexit(() => {
    //   startSectionTour(selectedOption);
    // });
    intro.start();
  };

  // Helper to start the tour for the current section (after selector)
  const startSectionTour = (section) => {
    if (section === "Category") {
      const intro = introJs();
      intro.setOptions({
        steps: [
          {
            element: '#category-name-input',
            intro: 'Enter the name for your new category here.',
            hidePrev: true
          },
          {
            element: '#parent-category-input',
            intro: 'Optionally, select a parent category for nesting.',
            hidePrev: true
          }
        ],
        exitOnOverlayClick: false,
        showBullets: false
      });
      intro.start();
    } else if (section === "Item") {
      const itemIntro = introJs();
      itemIntro.setOptions({
        steps: [
          {
            element: '#item-name-and-desc',
            intro: 'Enter the item name and an optional description.',
            hidePrev: true
          },
          {
            element: '#item-category-select',
            intro: 'Select the category for this item.',
            hidePrev: true
          },
          {
            element: '#item-price-input',
            intro: 'Enter the average price for this item.',
            hidePrev: true
          },
          {
            element: '#item-other-fields',
            intro: 'Specify the size/unit and pack size for this item.',
            hidePrev: true
          }
        ],
        exitOnOverlayClick: false,
        showBullets: false
      });
      itemIntro.start();
    } else if (section === "Variation") {
      const variationIntro = introJs();
      variationIntro.setOptions({
        steps: [
          {
            element: '#variation-name-and-item',
            intro: 'Enter the variation name and select the item this variation belongs to.',
            hidePrev: true
          },
          {
            element: '#variation-code-input',
            intro: 'Enter the product code and select the code type (UPC, PLU, ALTID).',
            hidePrev: true
          },
          {
            element: '#variation-price-input',
            intro: 'Enter the average price for this variation.',
            hidePrev: true
          }
        ],
        exitOnOverlayClick: false,
        showBullets: false
      });
      variationIntro.start();
    }
  };

  // --- Render modal UI ---
  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      aria-labelledby="create-item-variation-modal"
      centered
    >
      <Modal.Header>
        <div className="d-flex flex-wrap align-items-center justify-content-between w-100">
        {/* Close button for modal */}
            <Button
            variant="light"
            onClick={onHide}
            className="mb-2 ms-auto order-md-4"
            >
            <i className="fa fa-times"></i>
            </Button>
          {/* Question mark icon for tour (matches FoodPantry style) */}
          <span
            className="bi bi-question-circle d-none d-md-inline"
            style={{
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: '#4d59df',
              marginRight: '0.75rem',
              marginLeft: '0.25rem'
            }}
            title="Show a guided tour for this section"
            onClick={startTourForCurrentSelection}
            aria-label="Show guided tour"
          />
          <Modal.Title id="create-item-variation-modal">
            {/* Triple Selector for switching between Category, Item, and Variation forms */}
            <div className="triple-selector" id="category-item-variation-selector">
              <div className="switch">
                <input
                  id="switch-y"
                  name="tripple"
                  type="radio"
                  value="Category"
                  className="switch-input"
                  checked={selectedOption === "Category"}
                  onChange={() => setSelectedOption("Category")}
                />
                <label htmlFor="switch-y" className="switch-label switch-label-y">Category</label>
                <input
                  id="switch-i"
                  name="tripple"
                  type="radio"
                  value="Item"
                  className="switch-input"
                  checked={selectedOption === "Item"}
                  onChange={() => setSelectedOption("Item")}
                />
                <label htmlFor="switch-i" className="switch-label switch-label-i">Item</label>
                <input
                  id="switch-n"
                  name="tripple"
                  type="radio"
                  value="Variation"
                  className="switch-input"
                  checked={selectedOption === "Variation"}
                  onChange={() => setSelectedOption("Variation")}
                />
                <label htmlFor="switch-n" className="switch-label switch-label-n">Variation</label>
                <span className="switch-selector"></span>
              </div>
            </div>
          </Modal.Title>
          
        </div>
      </Modal.Header>
      <Modal.Body>


        {/* Category Creation Form */}
        {selectedOption === "Category" && (
          <>
            <h5 className="mb-2 mt-2">Create Category</h5>
            <form onSubmit={handleCategorySubmit} className="mt-3">
              <div className="row g-2">
                <div className="col-md-6 form-floating"
                  id="category-name-input">
                  <input
                    type="text"
                    placeholder="Category Name"
                    className="my-1 form-control"
                    name="name"
                    value={categoryForm.name || ""}
                    onChange={e => setCategoryForm({ ...categoryForm, name: e.target.value })}
                    onFocus={e => e.target.select()}
                    autoComplete="off"
                    required
                  />
                  <label htmlFor="text" className="form-label">
                    Category Name
                  </label>

                </div>
                <div className="col-md-6 form-floating"
                  id="parent-category-input">
                  <Select
                    classNamePrefix="react-select"
                    className="form-floating"
                    placeholder="Parent Category (optional)"
                    name="parentName"
                    value={
                      categoryForm.parentName
                        ? { value: categoryForm.parentName, label: categoryForm.parentName }
                        : null
                    }
                    onChange={option =>
                      setCategoryForm({ ...categoryForm, parentName: option ? option.value : "" })
                    }
                    options={allCategories.map(cat => ({
                      value: cat.name,
                      label: cat.name
                    }))}
                    isSearchable
                    isClearable
                  />
                  {/* <label htmlFor="parentName" className="form-label">
                    Parent Category (optional)
                  </label> */}
                </div>
              </div>
              <div className="d-flex justify-content-end my-2">
                <Button type="submit" variant="primary">
                  Create Category
                </Button>
              </div>
            </form>
          </>
        )}

        {/* Item Creation Form */}
        {selectedOption === "Item" && (
          <>
            <h5 className="mb-2 mt-4">Create Item</h5>
            <form onSubmit={handleItemSubmit} className="mt-3">
              <div className="row g-2"
                id="item-name-and-desc">
                <div className="col-md-6 form-floating">
                  <input
                    type="text"
                    placeholder="Item Name"
                    className="my-1 form-control"
                    name="name"
                    value={itemForm.name}
                    onChange={e => setItemForm({ ...itemForm, name: e.target.value })}
                    onFocus={e => e.target.select()}
                    autoComplete="off"
                    required
                    maxLength={255}
                  />
                  <label htmlFor="text" className="form-label">
                    Item Name
                  </label>
                </div>
                <div className="col-md-6 form-floating">
                  <input
                    type="text"
                    placeholder="Description (optional)"
                    className="my-1 form-control"
                    name="description"
                    value={itemForm.description}
                    onChange={e => setItemForm({ ...itemForm, description: e.target.value })}
                    onFocus={e => e.target.select()}
                    autoComplete="off"
                    maxLength={255}
                  />
                  <label htmlFor="text" className="form-label">
                    Description (optional)
                  </label>
                </div>
              </div>
              <div className="row g-2">
                <div className="col-md-3 form-floating" ref={itemFormCategoryRef}
                  id="item-category-select">
                  <Select
                    classNamePrefix="react-select"
                    className="form-floating"
                    placeholder="Category Name"
                    name="categoryName"
                    value={
                      itemForm.categoryName
                        ? { value: itemForm.categoryName, label: itemForm.categoryName }
                        : null
                    }
                    onChange={option =>
                      setItemForm({ ...itemForm, categoryName: option ? option.value : "" })
                    }
                    options={allCategories.map(cat => ({
                      value: cat.name,
                      label: cat.name
                    }))}
                    isSearchable
                    isClearable
                    required
                  />
                  {/* <label htmlFor="categoryName" className="form-label">
                    Category Name
                  </label> */}
                </div>
                {/* <input
                    type="text"
                    placeholder="Category Name"
                    className="my-1 form-control"
                    name="categoryName"
                    value={itemForm.categoryName}
                    onChange={handleItemFormInputChange}
                    onFocus={e => e.target.select()}
                    required
                    autoComplete="off"
                  />
                  {/* Category suggestions dropdown */}
                {/* {itemForm.categoryName && categorySuggestions.length > 0 && (
                    <ul className="list-group position-absolute" style={{ zIndex: 10, width: "100%" }}>
                      {categorySuggestions.map(category => (
                        <li
                          key={category.itemCategoryID}
                          className="list-group-item list-group-item-action"
                          style={{ cursor: "pointer" }}
                          onClick={() => handleCategorySuggestionClick(category)}
                        >
                          {category.name}
                        </li>
                      ))}
                    </ul>
                  )} */}
                <div className="col-md-3 form-floating"
                  id="item-price-input">
                  <CurrencyInput
                    name="suggestedPrice"
                    placeholder="Average Price"
                    className="my-1 form-control"
                    value={itemForm.suggestedPrice}
                    onValueChange={(value) => setItemForm({ ...itemForm, suggestedPrice: value })}
                    onFocus={(e) => e.target.select()}
                    autoComplete="off"
                    required
                    allowNegativeValue={false}
                    prefix="$"
                    transformRawValue={(rawValue) => {
                      // This logic ensures the "cash register" effect
                      const cleanValue = rawValue.replace(/\D/g, '');
                      const paddedValue = cleanValue.padStart(3, '0');
                      const cents = paddedValue.slice(-2);
                      const dollars = paddedValue.slice(0, -2);
                      return `${dollars}.${cents}`;
                    }}
                  />
                  <label htmlFor="text" className="form-label">
                    Price
                  </label>
                </div>
                <div className="col-md-6" id="item-other-fields">
                  <div className="row g-2">
                    <div className="col-md-6">
                      <div className="input-group">
                        <div className="form-floating">
                          <input
                            type="text"
                            placeholder="Size"
                            className="my-1 form-control"
                            name="unit"
                            value={itemForm.unit}
                            onChange={e => setItemForm({ ...itemForm, unit: e.target.value })}
                            onFocus={e => e.target.select()}
                            autoComplete="off"
                            required
                          />
                          <label htmlFor="text" className="form-label">
                            Size
                          </label>
                        </div>
                        <select
                          className="form-select"
                          value={itemForm.unitName}
                          onChange={e => setItemForm({ ...itemForm, unitName: e.target.value })}
                          required
                        >
                          <option value="" disabled hidden>Unit</option>
                          <option value="oz">oz</option>
                          <option value="fl oz">fl oz</option>
                          <option value="lbs">lbs</option>
                          <option value="gal">gal</option>
                          <option value="ct">ct</option>
                          <option value="g">g</option>
                          <option value="kg">kg</option>
                          <option value="qt">qt</option>
                          <option value="lt">lt</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-md-6 form-floating">
                      <input
                        type="text"
                        placeholder="Pack"
                        className="my-1 form-control"
                        name="packSize"
                        value={itemForm.packSize || ""}
                        onChange={e => setItemForm({ ...itemForm, packSize: e.target.value })}
                        onFocus={e => e.target.select()}
                        autoComplete="off"
                      />
                      <label htmlFor="text" className="form-label">
                        Pack
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="d-flex justify-content-end my-2">
                <Button type="submit" variant="primary">
                  Create Item
                </Button>
              </div>
            </form>
          </>
        )
        }

        {/* Variation Creation Form */}
        {
          selectedOption === "Variation" && (
            <>
              <h5 className="mb-2 mt-4">Create Variation</h5>
              <form onSubmit={handleVariationSubmit} className="mt-3">
                <div className="row g-2"
                  id="variation-name-and-item">
                  <div className="col-md-6 form-floating" ref={variationNameInputRef}>
                    <input
                      type="text"
                      placeholder="Variation Name"
                      className="my-1 form-control"
                      name="variationName"
                      value={variationForm.variationName}
                      onChange={handleVariationNameChange}
                      onFocus={e => e.target.select()}
                      autoComplete="off"
                      required
                      maxLength={50}
                    />
                    <label htmlFor="text" className="form-label">
                      Variation Name
                    </label>
                  </div>

                  {/* Variation suggestions dropdown removed for simplicity */}
                  <div className="col-md-6 form-floating" ref={itemNameInputRef}>
                    <Select
                      classNamePrefix="react-select"
                      className="form-floating"
                      placeholder="Item Name"
                      name="itemName"
                      value={
                        variationForm.itemName
                          ? { value: variationForm.itemName, label: variationForm.itemName }
                          : null
                      }
                      onChange={option =>
                        setVariationForm({ ...variationForm, itemName: option ? option.value : "" })
                      }
                      options={allItems.map(item => ({
                        value: item.name,
                        label: item.name
                      }))}
                      isSearchable
                      isClearable
                      required
                      styles={{
                        valueContainer: (provided) => ({
                          ...provided,
                          paddingLeft: 0 // Remove left padding
                        })
                      }}
                    />
                    {/* <label htmlFor="itemName" className="form-label">
                        Item Name
                      </label> */}
                    {/* <input
                    type="text"
                    placeholder="Item Name"
                    className="my-1 form-control"
                    name="itemName"
                    value={variationForm.itemName}
                    onChange={handleVariationItemNameChange}
                    onFocus={e => e.target.select()}
                    autoComplete="off"
                    required
                  />
                  <label htmlFor="text" className="form-label">
                    Item Name
                  </label>

                  {/* Item suggestions dropdown 
                  {variationForm.itemName && itemSuggestions.length > 0 && (
                    <ul className="list-group position-absolute" style={{ zIndex: 10, width: "100%" }}>
                      {itemSuggestions.map(item => (
                        <li
                          key={item.itemID}
                          className="list-group-item list-group-item-action"
                          style={{ cursor: "pointer" }}
                          onClick={() => handleItemSuggestionClick(item)}
                        >
                          {item.name}
                        </li>
                      ))}
                    </ul>
                  )} */}
                  </div>
                </div>
                <div className="row g-2">
                  {/* --- SWAPPED: CODE INPUT FIELD (with triple selector) FIRST ---*/}
                  <div className="col-md-6 form-floating"
                    id="variation-code-input">
                    {variationCodeType === "upc" && (
                      <>
                        <input
                          type="text"
                          placeholder="UPC (12 digits)"
                          className="my-1 form-control"
                          name="upc"
                          value={variationForm.upc}
                          onChange={handleVariationCodeInputChange}
                          maxLength={12}
                          pattern="\d{12}"
                          title="UPC must be 12 digits"
                          required
                        />
                        <label htmlFor="text" className="form-label">
                          UPC (12 digits)
                        </label>
                      </>
                    )}
                    {variationCodeType === "plu" && (
                      <>
                        <input
                          type="text"
                          placeholder="PLU (4 digits)"
                          className="my-1 form-control"
                          name="plu"
                          value={variationForm.plu}
                          onChange={handleVariationCodeInputChange}
                          maxLength={4}
                          pattern="\d{4}"
                          title="PLU must be 4 digits"
                          required
                        />
                        <label htmlFor="text" className="form-label">
                          PLU (4 digits)
                        </label>
                      </>
                    )}
                    {variationCodeType === "otherCode" && (
                      <>
                        <input
                          type="text"
                          placeholder="ALTID (5 digits)"
                          className="my-1 form-control"
                          name="otherCode"
                          value={variationForm.otherCode}
                          onChange={handleVariationCodeInputChange}
                          maxLength={5}
                          pattern="\d{1,5}"
                          title="ALTID must be 1-5 digits"
                          required
                        />
                        <label htmlFor="text" className="form-label">
                          ALTID (5 digits)
                        </label>
                      </>
                    )}
                    {/* --- TRIPLE SELECTOR DIRECTLY UNDER INPUT --- */}
                    <div className="d-flex justify-content-center mt-2">
                      <div className="triple-selector">
                        <div className="switch">
                          <input
                            id="switch-upc"
                            name="variationCodeType"
                            type="radio"
                            value="upc"
                            className="switch-input"
                            checked={variationCodeType === "upc"}
                            onChange={() => handleVariationCodeTypeChange("upc")}
                          />
                          <label htmlFor="switch-upc" className="switch-label switch-label-y">UPC</label>
                          <input
                            id="switch-plu"
                            name="variationCodeType"
                            type="radio"
                            value="plu"
                            className="switch-input"
                            checked={variationCodeType === "plu"}
                            onChange={() => handleVariationCodeTypeChange("plu")}
                          />
                          <label htmlFor="switch-plu" className="switch-label switch-label-i">PLU</label>
                          <input
                            id="switch-alt"
                            name="variationCodeType"
                            type="radio"
                            value="otherCode"
                            className="switch-input"
                            checked={variationCodeType === "otherCode"}
                            onChange={() => handleVariationCodeTypeChange("otherCode")}
                          />
                          <label htmlFor="switch-alt" className="switch-label switch-label-n">ALTID</label>
                          <span className="switch-selector"></span>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* --- SWAPPED: PRICE FIELD SECOND --- */}
                  <div className="col-md-6 form-floating"
                    id="variation-price-input">
                    <CurrencyInput
                      name="suggestedPrice"
                      placeholder="Average Price"
                      className="my-1 form-control"
                      value={variationForm.suggestedPrice}
                      onValueChange={(value) => setVariationForm({ ...variationForm, suggestedPrice: value })}
                      onFocus={(e) => e.target.select()}
                      autoComplete="off"
                      required
                      allowNegativeValue={false}
                      prefix="$"
                      transformRawValue={(rawValue) => {
                        // This logic ensures the "cash register" effect
                        const cleanValue = rawValue.replace(/\D/g, '');
                        const paddedValue = cleanValue.padStart(3, '0');
                        const cents = paddedValue.slice(-2);
                        const dollars = paddedValue.slice(0, -2);
                        return `${dollars}.${cents}`;
                      }}
                    />
                    <label htmlFor="text" className="form-label">
                      Price
                    </label>
                  </div>
                </div>
                <div className="d-flex justify-content-end my-2">
                  <Button type="submit" variant="primary">
                    Create Variation
                  </Button>
                </div>
              </form>
            </>
          )
        }
      </Modal.Body >
    </Modal >
  );
}
